import React from 'react';
import { LocationItem, LocationSelector } from './LocationSelector';
import { Icons } from '@/icons';
import { Range } from 'react-range';

const MIN = 0;
const MAX = 100;

type AudienceLocation = {
  country: string;
  sub_region: string | null;
  city: string | null;
};

export interface Audience {
  id: string;
  title: string;
  gender: string;
  ageRange: [number, number];
  min_age: number;
  max_age: number;
  audience_locations: AudienceLocation[];
}

interface Props {
  audiences: Audience[];
  selectedId: string;
  newName: string;
  onSelect: (id: string) => void;
  onNewName: (name: string) => void;
  newGender: string[];
  setNewGender: (gender: string[]) => void;
  newAgeRange: [number, number];
  setNewAgeRange: (range: [number, number]) => void;
  locations: LocationItem[];
  setLocations: (location: LocationItem[]) => void;
}

export const AudienceSelector: React.FC<Props> = ({
  audiences,
  selectedId,
  newName,
  onSelect,
  onNewName,
  newGender,
  setNewGender,
  newAgeRange,
  setNewAgeRange,
  locations,
  setLocations,
}) => {
  return (
    <details className='group rounded-xl overflow-visible bg-[#F5F5F5] p-4'>
      {/* Header / Summary */}
      <summary className='flex items-center justify-between bg-[#F5F5F5] text-lg font-medium cursor-pointer'>
        <span className='font-normal text-base'>Audience</span>
        <span className='transform rotate-180 transition-transform duration-200 group-open:rotate-0 mr-4'>
          <Icons.ChivronUp />
        </span>
      </summary>

      {/* Divider line */}
      <hr className='mt-4 mb-2 border-t border-gray-400' />

      <div className='text-base'>Recent Campaigns Audience</div>

      {/* Content */}
      <div className='mt-2 space-y-4 bg-[#F5F5F5]'>
        {/* Existing audiences */}
        {audiences.map((a) => (
          <label
            key={a.id}
            className='flex items-center gap-4 p-3 bg-white rounded-lg'
          >
            <input
              type='radio'
              name='audience'
              className='w-5 h-5 accent-black'
              checked={selectedId === a.id}
              onChange={() => {
                onSelect(a.id);
                onNewName('');
              }}
            />
            <span className='text-gray-900 text-base'>{a.title}</span>
          </label>
        ))}

        <span className='text-sm w-full flex justify-center'>
          Or Create New
        </span>

        {/* “Create New” option */}
        <label className='flex items-center gap-4 p-3 bg-white rounded-lg'>
          <input
            type='radio'
            name='audience'
            className='w-5 h-5 accent-black'
            checked={!selectedId}
            onChange={() => {
              onSelect('');
            }}
          />
          <span className='text-gray-900 text-base'>Create New</span>
        </label>

        {/* Input for new audience */}
        <input
          type='text'
          value={newName}
          onChange={(e) => onNewName(e.target.value)}
          placeholder='New Audience Name'
          className='w-full border-b border-gray-300 px-3 py-2'
        />

        {/* Extra fields shown only when creating new */}
        {!selectedId && (
          <div className='space-y-8'>
            {/* Gender */}
            <div>
              <label className='block text-sm font-medium mb-1'>Gender</label>
              <div className='grid grid-cols-3 gap-3'>
                {['male', 'female', 'other'].map((gender) => (
                  <label
                    key={gender}
                    className='flex items-center gap-2 bg-white px-5 py-5 rounded-xl'
                  >
                    <input
                      type='checkbox'
                      value={gender}
                      checked={newGender.includes(gender)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewGender([...newGender, gender]);
                        } else {
                          setNewGender(newGender.filter((g) => g !== gender));
                        }
                      }}
                      className='w-5 h-5 accent-[#FF0050] cursor-pointer'
                    />
                    <span className='capitalize text-gray-800'>{gender}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Age Range */}
            <div>
              <label className='block text-sm font-normal mb-3'>Age</label>
              <div className='flex items-center space-x-4 w-full'>
                {/* Left Value */}
                <span className='text-lg font-normal w-[20px]'>
                  {newAgeRange[0]}
                </span>

                {/* Slider */}
                <Range
                  step={1}
                  min={MIN}
                  max={MAX}
                  values={newAgeRange}
                  onChange={(values) =>
                    setNewAgeRange(values as [number, number])
                  }
                  renderTrack={({ props, children }) => (
                    <div
                      {...props}
                      className='h-4 flex-1 flex items-center'
                      style={{ position: 'relative' }}
                    >
                      {/* Background Track */}
                      <div className='h-[14px] w-full rounded-full bg-[#E9E9E9]' />

                      {/* Active Range */}
                      <div
                        className='absolute h-[14px] rounded-sm bg-[#FF0050]'
                        style={{
                          left: `${((newAgeRange[0] - MIN + 1.5) / (MAX - MIN)) * 100}%`,
                          width: `${((newAgeRange[1] - newAgeRange[0] - 3.3) / (MAX - MIN)) * 100}%`,
                        }}
                      />

                      {/* Vertical Pink Bars */}
                      <div
                        className='absolute w-[5px] bg-[#FF0050] rounded-lg'
                        style={{
                          left: `${1}%`,
                          top: '+5.5px',
                          bottom: '+5.5px',
                        }}
                      />
                      <div
                        className='absolute w-[5px] bg-[#FF0050] rounded-lg'
                        style={{
                          left: `${98}%`,
                          top: '+5.5px',
                          bottom: '+5.5px',
                        }}
                      />

                      {children}
                    </div>
                  )}
                  renderThumb={({ props }) => {
                    const { key, ...rest } = props;
                    return (
                      <div
                        key={key}
                        {...rest}
                        className='w-[4px] h-10 rounded-full bg-[#FF0050] cursor-pointer mt-6'
                      />
                    );
                  }}
                />

                {/* Right Value */}
                <span className='text-lg font-normal w-[20px]'>
                  {newAgeRange[1] === MAX ? '100' : newAgeRange[1]}
                </span>
              </div>
            </div>

            {/* Location Selector */}
            <div className='bg-white rounded-xl p-3'>
              <label className='block text-sm font-normal mb-3'>Location</label>
              <LocationSelector onChange={setLocations} />
            </div>
          </div>
        )}
      </div>
    </details>
  );
};
